import moment, { Moment } from 'moment';
import { Awaited } from '../../../types/typescript';
import type { SelectedMealPlan, GuestCount } from '../../components/booking/meal-plan/types';
import searchHotels from '../hotel/searchHotels';
import getHotelRoomsFromApi from '../hotel/getHotelRooms';
import type { RootState } from '../redux/store';
import { COMMON_DATE_FORMAT } from '../constants';

type Hotel = Awaited<ReturnType<typeof searchHotels>>['data'][0];
type HotelRoom = Awaited<ReturnType<typeof getHotelRoomsFromApi>>[0];
type MealPlan = NonNullable<HotelRoom['mealPlans']>[0];

const getMealPlanOptions = (selectedPlan: MealPlan, hotelRoom: HotelRoom) => {
  if (!selectedPlan.meal || !hotelRoom.mealPlans) return [];
  const { breakfast, lunch, dinner } = selectedPlan.meal;
  const mealCount = [breakfast, lunch, dinner].filter((m) => m).length;

  /**
   * if selected category on previous page is a single-meal plan such as breakfast, lunch, or dinner,
   * return all meal plans which includes the meal
   */
  if (mealCount === 1) {
    const filteredMealPlanOptions: MealPlan[] = [];
    hotelRoom.mealPlans?.forEach((p) => {
      if (breakfast && p.meal?.breakfast) {
        filteredMealPlanOptions.push(p);
      }
      if (lunch && p.meal?.lunch) {
        filteredMealPlanOptions.push(p);
      }
      if (dinner && p.meal?.dinner) {
        filteredMealPlanOptions.push(p);
      }
    });

    return Array.from(new Set(filteredMealPlanOptions));
  }

  /**
   * if selected category on previous page is a multi-meal plan such as breakfast+lunch, breakfast+dinner, or lunch+dinner,
   * return only the multi-meal plan which falls into the category plus any plans provide all meals breakfast+lunch+dinner if applicable
   */
  if (mealCount === 2) {
    return hotelRoom.mealPlans?.filter(
      (m) =>
        JSON.stringify(m.meal) === JSON.stringify(selectedPlan.meal) ||
        JSON.stringify(m.meal) === JSON.stringify({ breakfast: true, lunch: true, dinner: true })
    );
  }

  /**
   * if selected category on previous page provides all meals e.g. breakfast+lunch+dinner,
   * return only plans provides all meals e.g. meal: { breakfast: true, lunch: true, dinner: true}
   */
  if (mealCount === 3) {
    return hotelRoom.mealPlans?.filter(
      (m) => JSON.stringify(m.meal) === JSON.stringify(selectedPlan.meal)
    );
  }

  // if selected category on previous page is no meal (mealCount === 0), return all meal plan options
  return hotelRoom?.mealPlans;
};

const getSelectedMealPlans = (
  planOptions: MealPlan[],
  stayNights: number,
  checkInDate: Moment,
  roomCount: number,
  guestCount: GuestCount,
  source: string,
  mealPlanId: string
) => {
  if (!stayNights || stayNights < 1) return [];

  return [...Array(stayNights)].reduce<SelectedMealPlan[]>((acc, _, index) => {
    const date = moment(checkInDate.add(index ? 1 : 0, 'days')).format(COMMON_DATE_FORMAT);
    // if query string source=google_hotel_ads is passed in, set the plan specified by plan-id query param even when it has multiple plan options to choose from
    if (source === 'google_hotel_ads') {
      const a = acc.concat({
        id: index + 1,
        mealPlan: planOptions.find((mealPlan) => mealPlan.id === mealPlanId),
        count: {
          roomCount,
          guestCount,
        },
        date,
      } as SelectedMealPlan);

      return a;
    }

    const a = acc.concat(
      planOptions.length === 1
        ? ({
            id: index + 1,
            mealPlan: planOptions[0],
            count: {
              roomCount,
              guestCount,
            },
            date,
          } as SelectedMealPlan)
        : ({
            id: index + 1,
            mealPlan: planOptions.find((mealPlan) => mealPlan.id === mealPlanId),
            count: {
              roomCount,
              guestCount,
            },
            date,
          } as SelectedMealPlan)
    );

    return a;
  }, [] as SelectedMealPlan[]);
};

const initiatePlanConfig = (
  state: RootState['planConfig'],
  payload: {
    selectedHotel: Hotel;
    selectedHotelRoom: HotelRoom;
    initiallySelectedMealPlanId: string;
    tllPlanCode: string;
    stayNights: number;
    checkInDate: string;
    roomCount: number;
    guestCount: GuestCount;
    source: string;
    mealPlanId: string;
  }
) => {
  const {
    selectedHotel,
    selectedHotelRoom,
    initiallySelectedMealPlanId,
    tllPlanCode,
    stayNights,
    checkInDate,
    roomCount,
    guestCount,
    source,
    mealPlanId,
  } = payload;
  const initiallySelectedMealPlan = selectedHotelRoom.mealPlans?.find(
    (m) => m.id === (initiallySelectedMealPlanId || tllPlanCode)
  );
  const mealOptions =
    selectedHotelRoom.mealPlans?.filter(
      (m) => JSON.stringify(m.meal) === JSON.stringify(initiallySelectedMealPlan?.meal)
    ) || [];
  const selectedMealPlans = getSelectedMealPlans(
    mealOptions,
    stayNights,
    moment(checkInDate),
    roomCount,
    guestCount,
    source,
    mealPlanId
  );

  state.selectedHotel = selectedHotel;
  state.selectedHotelRoom = selectedHotelRoom;

  if (selectedMealPlans.length) {
    state.selectedMealPlans = selectedMealPlans;
  } else {
    state.isFailedToCreateInitialSelectedMealPlans = true;
  }

  if (initiallySelectedMealPlan) {
    state.initiallySelectedMealPlan = initiallySelectedMealPlan;
    state.mealPlanOptions = getMealPlanOptions(initiallySelectedMealPlan, selectedHotelRoom);

    // if there is no meal plan options to select or plans are all set for all nights, hide the banner
    state.isBannerHidden =
      state.mealPlanOptions.length < 2 ||
      !selectedMealPlans.every((selectedMealPlan) => selectedMealPlan.mealPlan);

    // if there is no meal plan options to select, hide the change meal plan button
    state.isChangeMealPlanButtonHidden = state.mealPlanOptions.length === 1;
  } else {
    state.isFailedToCreateInitialSelectedMealPlans = true;
  }
};

export default initiatePlanConfig;
